//frontend/src/pages/company/Warehouses.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import CompanyLayout from '../../components/layout/CompanyLayout';
import { warehouseAPI, districtAPI } from '../../services/api';

const Warehouses = () => {
  const { user, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    warehouse_name: '',
    address: '',
    district_id: '',
    status: 'active'
  });

  // Max warehouses from company (default 3)
  const maxWarehouses = user?.company?.max_warehouses || 1;
  const canCreateWarehouse = warehouses.length < maxWarehouses;

  useEffect(() => {
    fetchWarehouses();
    fetchDistricts();
  }, [searchTerm, statusFilter]);

  const fetchWarehouses = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      
      const response = await warehouseAPI.getAll(params);
      setWarehouses(response.data.data);
    } catch (error) {
      console.error('Fetch warehouses error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await districtAPI.getAll();
      setDistricts(response.data.data || []);
    } catch (error) {
      console.error('Fetch districts error:', error);
      setDistricts([]);
    }
  };

  const handleCreate = () => {
    if (!canCreateWarehouse) {
      alert(`Лимит хэтэрсэн! Таны компани зөвхөн ${maxWarehouses} агуулах үүсгэх эрхтэй.`);
      return;
    }
    setEditingWarehouse(null);
    setFormData({
      warehouse_name: '',
      address: '',
      district_id: '',
      status: 'active'
    });
    setShowModal(true);
  };

  const handleEdit = (warehouse) => {
    setEditingWarehouse(warehouse);
    setFormData({
      warehouse_name: warehouse.warehouse_name,
      address: warehouse.address || '',
      district_id: warehouse.district_id || '',
      status: warehouse.status
    });
    setShowModal(true);
  };

  const handleViewDetails = (id) => {
    navigate(`/company/warehouses/${id}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        district_id: formData.district_id && formData.district_id !== '' ? parseInt(formData.district_id) : null
      };

      if (editingWarehouse) {
        await warehouseAPI.update(editingWarehouse.id, submitData);
        alert('Агуулах амжилттай шинэчлэгдлээ');
      } else {
        await warehouseAPI.create(submitData);
        alert('Агуулах амжилттай үүслээ');
      }
      setShowModal(false);
      fetchWarehouses();
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Энэ агуулахыг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await warehouseAPI.delete(id);
      alert('Агуулах амжилттай устлаа');
      fetchWarehouses();
    } catch (error) {
      alert(error.response?.data?.message || 'Устгахад алдаа гарлаа');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    try {
      await warehouseAPI.toggleStatus(id, newStatus);
      fetchWarehouses();
    } catch (error) {
      alert(error.response?.data?.message || 'Статус өөрчлөхөд алдаа гарлаа');
    }
  };

  const getDistrictName = (districtId) => {
    const district = districts.find(d => d.id === districtId);
    return district ? district.name : '-';
  };

  if (loading && warehouses.length === 0) {
    return (
      <CompanyLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </CompanyLayout>
    );
  }

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* INFO BANNER & CREATE BUTTON */}
        <div className="flex items-center justify-between gap-4">
          {/* Info Banner */}
          <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
            <span className="text-lg">ℹ️</span>
            <span className="text-sm font-medium text-gray-900">
              Агуулахын мэдээлэл
            </span>
            <span className={`text-sm font-bold ${canCreateWarehouse ? 'text-green-700' : 'text-red-600'}`}>
              {warehouses.length}/{maxWarehouses}
            </span>
          </div>

          {/* Create Button */}
          {hasPermission('warehouse.create') && (
            <button 
              onClick={handleCreate}
              disabled={!canCreateWarehouse}
              className={`btn flex items-center gap-2 ${canCreateWarehouse ? 'btn-primary' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              title={!canCreateWarehouse ? `Лимит хэтэрсэн (${maxWarehouses} агуулах)` : ''}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Агуулах нэмэх</span>
            </button>
          )}
        </div>

        {/* SEARCH & FILTER */}
        <div className="flex flex-col md:flex-row md:items-center gap-4 bg-white p-4 rounded-xl border border-gray-200">
          <div className="flex-1">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Агуулахын нэрээр хайх..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
              />
              <svg className="absolute left-3 top-3 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)} 
              className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Бүх статус</option>
              <option value="active">Идэвхтэй</option>
              <option value="inactive">Идэвхгүй</option>
            </select>
          </div>
        </div>

        {/* EMPTY STATE */}
        {warehouses.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <div className="text-6xl mb-4">🏭</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Агуулах байхгүй байна</h3>
            <p className="text-gray-600 mb-6">Эхний агуулахаа нэмээд эхлээрэй</p>
            {hasPermission('warehouse.create') && (
              <button 
                onClick={handleCreate}
                disabled={!canCreateWarehouse}
                className={`btn ${canCreateWarehouse ? 'btn-primary' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
              >
                + Агуулах нэмэх
              </button>
            )}
          </div>
        ) : (
          /* TABLE */
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">№</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Нэр</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Хаяг</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дүүрэг</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Шоп</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Бараа</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Үлдэгдэл</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Үйлдэл</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {warehouses.map((warehouse, index) => (
                    <tr key={warehouse.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <span className="text-xl">🏭</span>
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900">{warehouse.warehouse_name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                        {warehouse.address || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {getDistrictName(warehouse.district_id)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-semibold">{warehouse.shop_count || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-semibold">{warehouse.product_count || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-semibold text-green-600">{warehouse.total_stock || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          warehouse.status === 'active' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {warehouse.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          {/* Дэлгэрэнгүй харах */}
                          <button
                            onClick={() => handleViewDetails(warehouse.id)}
                            className="text-blue-600 hover:text-blue-800 p-1 hover:bg-blue-50 rounded transition"
                            title="Дэлгэрэнгүй"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                          </button>
                          
                          {/* Засах */}
                          {hasPermission('warehouse.edit') && (
                            <button
                              onClick={() => handleEdit(warehouse)}
                              className="text-gray-600 hover:text-gray-800 p-1 hover:bg-gray-50 rounded transition"
                              title="Засах"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Статус өөрчлөх */}
                          {hasPermission('warehouse.edit') && (
                            <button
                              onClick={() => handleToggleStatus(warehouse.id, warehouse.status)}
                              className={`p-1 hover:bg-gray-50 rounded transition ${
                                warehouse.status === 'active' ? 'text-gray-600 hover:text-gray-800' : 'text-green-600 hover:text-green-800'
                              }`}
                              title={warehouse.status === 'active' ? 'Хаах' : 'Нээх'}
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                            </button>
                          )}
                          
                          {/* Устгах */}
                          {hasPermission('warehouse.delete') && (
                            <button
                              onClick={() => handleDelete(warehouse.id)}
                              className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded transition"
                              title="Устгах"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* CREATE/EDIT MODAL */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <span className="text-3xl">🏭</span>
              <span>{editingWarehouse ? 'Агуулах засах' : 'Агуулах нэмэх'}</span>
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Агуулахын нэр *
                </label>
                <input 
                  type="text" 
                  value={formData.warehouse_name} 
                  onChange={(e) => setFormData({ ...formData, warehouse_name: e.target.value })} 
                  className="admin-input" 
                  required 
                  placeholder="Жнь: Central Warehouse" 
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Дүүрэг (Сонголт)
                </label>
                <select 
                  value={formData.district_id} 
                  onChange={(e) => setFormData({ ...formData, district_id: e.target.value })} 
                  className="admin-input"
                >
                  <option value="">-- Дүүрэг сонгох --</option>
                  {districts.map((district) => (
                    <option key={district.id} value={district.id}>
                      {district.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Хаяг
                </label>
                <textarea 
                  value={formData.address} 
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })} 
                  className="admin-input" 
                  rows="3" 
                  placeholder="Дэлгэрэнгүй хаяг"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Статус</label>
                <div className="flex items-center gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="active" 
                      checked={formData.status === 'active'} 
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                      className="mr-2" 
                    />
                    <span className="text-sm">Идэвхтэй</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="radio" 
                      name="status" 
                      value="inactive" 
                      checked={formData.status === 'inactive'} 
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })} 
                      className="mr-2" 
                    />
                    <span className="text-sm">Идэвхгүй</span>
                  </label>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Болих
                </button>
                <button 
                  type="submit" 
                  className="flex-1 btn btn-primary"
                >
                  ✅ Хадгалах
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default Warehouses;